import {
  apply,
  coordinates,
  type CoordinateTransformContext,
  type Point2D,
  type Rectangle2D,
} from '../../../canvas/core';
import type { PageLayoutData } from '../layout/types';

/**
 * Coordinate transforms between content space and page space.
 *
 * Page space: coordinates relative to a specific page's top-left corner (0,0).
 * Points can be negative if they lie outside the page bounds.
 *
 * Page rendering uses this transform hierarchy (in SVG nested groups):
 * <g transform="translate(centerX, centerY)">       ← Applied last (outermost)
 *   <g transform="rotate(rotation)">                ← Applied second
 *     <g transform="translate(-w/2, -h/2)">         ← Applied first (innermost)
 *
 * In SVG, nested transforms apply from innermost outward, resulting in:
 * M_svg = T(centerX, centerY) · R(rotation) · T(-w/2, -h/2)
 *
 * DOMMatrix chains post-multiply, so to match SVG we call in the SAME order:
 * new DOMMatrix().translate(center).rotate(θ).translate(-half)
 */

export type PageGeometry = {
  rotation: number;
};

export type PageCoordinateTransformContext = {
  layout: PageLayoutData;
  geometry: PageGeometry;
};

type StateWithGeometryFeatures = {
  rotation?: {
    values: Record<number, number>;
    animations: Record<number, { visualRotation?: number } | undefined>;
  };
};

export const getPageCoordinateTransformContext = (
  state: StateWithGeometryFeatures,
  layout: PageLayoutData
): PageCoordinateTransformContext => ({
  layout,
  geometry: {
    rotation:
      state.rotation?.animations[layout.pageId]?.visualRotation ??
      state.rotation?.values[layout.pageId] ??
      0,
  },
});

const pageToContentMatrix = ({
  layout,
  geometry,
}: PageCoordinateTransformContext): DOMMatrixReadOnly => {
  const centerX = layout.x + layout.width / 2;
  const centerY = layout.y + layout.height / 2;

  return new DOMMatrix()
    .translate(centerX, centerY)
    .rotate(geometry.rotation)
    .translate(-layout.originalWidth / 2, -layout.originalHeight / 2);
};

const contentToPageMatrix = (
  ctx: PageCoordinateTransformContext
): DOMMatrixReadOnly => pageToContentMatrix(ctx).inverse();

export const pageCoordinates = {
  point: {
    contentToPage: (ctx: PageCoordinateTransformContext) =>
      apply.point(contentToPageMatrix(ctx)),

    pageToContent: (ctx: PageCoordinateTransformContext) =>
      apply.point(pageToContentMatrix(ctx)),

    screenToPage:
      (
        canvasCtx: CoordinateTransformContext,
        pageCtx: PageCoordinateTransformContext
      ) =>
      (p: Point2D): Point2D => {
        const contentPoint = coordinates.point.screenToContent(canvasCtx)(p);
        return pageCoordinates.point.contentToPage(pageCtx)(contentPoint);
      },
  },

  vector: {
    contentToPage: (ctx: PageCoordinateTransformContext) =>
      apply.vector(contentToPageMatrix(ctx)),

    pageToContent: (ctx: PageCoordinateTransformContext) =>
      apply.vector(pageToContentMatrix(ctx)),
  },

  scalar: {
    contentToPage: (ctx: PageCoordinateTransformContext) =>
      apply.scalar(contentToPageMatrix(ctx)),

    pageToContent: (ctx: PageCoordinateTransformContext) =>
      apply.scalar(pageToContentMatrix(ctx)),
  },

  rect: {
    contentToPage: (ctx: PageCoordinateTransformContext) =>
      apply.rect(contentToPageMatrix(ctx)),

    pageToContent: (ctx: PageCoordinateTransformContext) =>
      apply.rect(pageToContentMatrix(ctx)),

    screenToPage:
      (
        canvasCtx: CoordinateTransformContext,
        pageCtx: PageCoordinateTransformContext
      ) =>
      (r: Rectangle2D): Rectangle2D => {
        const contentRect = coordinates.rect.screenToContent(canvasCtx)(r);
        return pageCoordinates.rect.contentToPage(pageCtx)(contentRect);
      },
  },
};
